import { normalizarDocumento, normalizarIdComparacao } from "./documento.js";

/**
 * Roles reconhecidos pelo sistema (espelho das constantes usadas no
 * CadastroEmpresa e em outras páginas internas). Mantidos localmente
 * para evitar acoplamento e permitir testes unitários.
 */
export const NIVEL_EMPRESA = 1;
export const NIVEL_ADMIN_VISUALIZADOR = 2;
export const NIVEL_CONSULTOR = 3;
export const NIVEL_SUPEX = 4;

/**
 * Estudante (nível 1 do tipo_acesso do CIEEMG, ou `acesso_autorizacao === 1`
 * no JWT). Vê apenas as autorizações vinculadas ao próprio CPF — uma por
 * empresa — e cada item é tratado de forma individual.
 */
export const NIVEL_ESTUDANTE = 10;

/**
 * Identifica se o usuário logado é um estudante do fluxo TCE/TCA
 * Empregador. Considera tanto o JWT quanto o fallback no localStorage
 * para evitar divergências entre as duas fontes.
 */
export function usuarioEhEstudante(payloadJwt = {}) {
  if (payloadJwt && typeof payloadJwt === "object") {
    if (payloadJwt.tipo === "estudante" || payloadJwt.tipo === "aprendiz") return true;
    if (Number(payloadJwt.acesso_autorizacao ?? 0) === 1) return true;
    if (Number(payloadJwt.tipo_acesso ?? 0) === 1) return true;
    if (Number(payloadJwt.nivel_acesso ?? 0) === 1) return true;
    if (Number(payloadJwt.role_id ?? 0) === 1) return true;
    if (Number(payloadJwt.role ?? 0) === 1) return true;
  }
  if (typeof window !== "undefined") {
    try {
      const rawUser = window.localStorage.getItem("user") || window.localStorage.getItem("usuario");
      if (rawUser) {
        const u = JSON.parse(rawUser);
        if (u?.tipo === "estudante" || u?.tipo === "aprendiz") return true;
        if (Number(u?.acesso_autorizacao ?? 0) === 1) return true;
        if (Number(u?.tipo_acesso ?? 0) === 1) return true;
        if (Number(u?.nivel_acesso ?? 0) === 1) return true;
        if (Number(u?.role_id ?? 0) === 1) return true;
        if (Number(u?.role ?? 0) === 1) return true;
      }
    } catch (_) {}
  }
  return false;
}

/**
 * Identifica se o usuário logado possui perfil CIEE / Administrador / Operador
 * (SEOPE, SECEX, INFOR, SUPEX ou qualquer role de gestão/validação >= 2).
 */
export function usuarioEhCiee(payloadJwt = {}, identidade = {}) {
  if (usuarioEhEstudante(payloadJwt)) return false;

  const tipoJwt = String(payloadJwt?.tipo || "").toLowerCase();
  const tipoIdentidade = String(identidade?.tipo || "").toLowerCase();

  if (tipoJwt === "usuario_ciee" || tipoIdentidade === "usuario_ciee" || tipoJwt === "ciee") {
    return true;
  }

  const candidatosNivel = [
    payloadJwt?.acesso_autorizacao,
    payloadJwt?.tipo_acesso,
    payloadJwt?.nivel_acesso,
    payloadJwt?.role_id,
    payloadJwt?.role,
    identidade?.acesso_autorizacao,
    identidade?.tipo_acesso,
    identidade?.nivel_acesso,
    identidade?.role_id,
    identidade?.role,
    identidade?.nivel,
  ];

  for (const c of candidatosNivel) {
    const num = Number(c);
    if (Number.isFinite(num) && num >= 2) {
      return true;
    }
  }

  if (typeof window !== "undefined") {
    try {
      const rawUser = window.localStorage.getItem("user") || window.localStorage.getItem("usuario");
      if (rawUser) {
        const u = JSON.parse(rawUser);
        const tipoU = String(u?.tipo || "").toLowerCase();
        if (tipoU === "usuario_ciee" || tipoU === "ciee") return true;
        const candidatosU = [
          u?.acesso_autorizacao,
          u?.tipo_acesso,
          u?.nivel_acesso,
          u?.role_id,
          u?.role,
        ];
        for (const c of candidatosU) {
          const num = Number(c);
          if (Number.isFinite(num) && num >= 2) return true;
        }
      }
    } catch (_) {}
  }

  return false;
}

const CAMPOS_USUARIO_ID = [
  "usuario_id",
  "id_usuario",
  "usuarioId",
  "idUsuario",
  "user_id",
  "id",
  "ID",
  "created_by",
  "createdBy",
  "criado_por",
  "criadoPor",
  "cadastrado_por",
  "cadastradoPor",
  "id_cadastrador",
  "idCadastrador",
  "usuario_cadastro_id",
  "usuarioCadastroId",
  "consultor_id",
  "consultorId",
  "id_consultor",
  "idConsultor",
];

const CAMPOS_USUARIO_DOCUMENTO = [
  "documento_usuario",
  "documentoUsuario",
  "documento_cadastrador",
  "documentoCadastrador",
  "cadastrado_por_documento",
  "cadastradoPorDocumento",
  "usuario_documento",
  "usuarioDocumento",
  "cpf_usuario",
  "cpfUsuario",
  "cnpj_usuario",
  "cnpjUsuario",
  "documento",
  "cpf",
  "cnpj",
  "CPF",
  "CNPJ",
];

const SUB_OBJETOS = ["usuario", "cadastrador", "responsavel", "empresa", "cadastro"];

function flattenValores(obj, profundidadeMax = 3, profundidade = 0, acumulado = []) {
  if (!obj || typeof obj !== "object" || profundidade > profundidadeMax)
    return acumulado;

  if (Array.isArray(obj)) {
    obj.forEach((item) =>
      flattenValores(item, profundidadeMax, profundidade + 1, acumulado),
    );
    return acumulado;
  }

  for (const [chave, valor] of Object.entries(obj)) {
    if (
      CAMPOS_USUARIO_ID.includes(chave) ||
      CAMPOS_USUARIO_DOCUMENTO.includes(chave)
    ) {
      acumulado.push(valor);
    } else if (SUB_OBJETOS.includes(chave) && valor && typeof valor === "object") {
      flattenValores(valor, profundidadeMax, profundidade + 1, acumulado);
    }
  }

  return acumulado;
}

/**
 * Verifica se a empresa foi cadastrada pelo consultor logado,
 * tolerando variações no payload do backend.
 */
export function empresaFoiCadastradaPeloConsultor(
  item,
  usuarioIdLogado,
  documentoLogado,
) {
  const raw = item?.raw || item || {};

  const usuarioIdNormalizado = normalizarIdComparacao(usuarioIdLogado);
  const documentoNormalizado = normalizarDocumento(documentoLogado);

  if (!usuarioIdNormalizado && !documentoNormalizado) return false;

  const valores = flattenValores(raw);

  if (usuarioIdNormalizado) {
    const matchPorId = valores
      .filter((valor) => valor !== null && String(valor).trim())
      .map(normalizarIdComparacao)
      .some((id) => id && id === usuarioIdNormalizado);

    if (matchPorId) return true;
  }

  if (documentoNormalizado) {
    const matchPorDocumento = valores
      .map(normalizarDocumento)
      .some((doc) => doc && doc === documentoNormalizado);

    if (matchPorDocumento) return true;
  }

  return false;
}
